// Cenovka — tipni novou cenu 2026/27 a rozhodni, jestli má hráč value.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Tag, Timer, CheckCircle, XCircle, ArrowRight } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { useAuth } from "../contexts/AuthContext";
import { funPriceRound, funResult } from "../lib/lab-api";

const DIFFS = [
    { key: "easy", label: "Lehká", desc: "Známí hráči · 20 s na tip", color: "border-fa-mint/50 text-fa-mint" },
    { key: "medium", label: "Střední", desc: "Střední pool · 12 s na tip", color: "border-fa-yellow/50 text-fa-yellow" },
    { key: "hard", label: "Těžká", desc: "Neznámí hráči · 8 s na tip", color: "border-red-400/50 text-red-300" },
];

const STAT_LABELS = [["points", "Body 25/26"], ["minutes", "Minuty"], ["goals", "Góly"], ["assists", "Asistence"], ["bonus", "Bonusy"], ["clean_sheets", "Čistá konta"]];

export default function PriceGuess() {
    const { user } = useAuth();
    const [difficulty, setDifficulty] = useState(null);
    const [round, setRound] = useState(null);
    const [phase, setPhase] = useState("price"); // price | value | done
    const [guess, setGuess] = useState(6.0);
    const [priceCorrect, setPriceCorrect] = useState(null);
    const [valueCorrect, setValueCorrect] = useState(null);
    const [timeLeft, setTimeLeft] = useState(0);
    const [score, setScore] = useState({ pts: 0, rounds: 0, streak: 0 });
    const timerRef = useRef(null);
    const lastIdRef = useRef(null);

    const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };

    const next = useCallback((diff) => {
        stopTimer();
        setRound(null); setPhase("price"); setGuess(6.0); setPriceCorrect(null); setValueCorrect(null);
        funPriceRound({ difficulty: diff, exclude: lastIdRef.current || undefined })
            .then((d) => {
                lastIdRef.current = d.player.fpl_id;
                setRound(d);
                setTimeLeft(d.time_limit);
            })
            .catch(() => toast.error("Načtení kola selhalo"));
    }, []);

    useEffect(() => () => stopTimer(), []);

    // Odpočet — běží jen ve fázi tipování ceny.
    useEffect(() => {
        if (!round || phase !== "price") { stopTimer(); return; }
        timerRef.current = setInterval(() => {
            setTimeLeft((t) => {
                if (t <= 0.1) {
                    stopTimer();
                    setPriceCorrect(false);
                    setPhase("value");
                    setScore((s) => ({ ...s, rounds: s.rounds }));
                    return 0;
                }
                return Math.round((t - 0.1) * 10) / 10;
            });
        }, 100);
        return stopTimer;
    }, [round, phase]);

    const submitPrice = async () => {
        if (!round || phase !== "price") return;
        stopTimer();
        const ok = Math.abs(guess - round.price) <= round.tolerance;
        setPriceCorrect(ok);
        setPhase("value");
        if (ok) {
            setScore((s) => ({ ...s, pts: s.pts + 1 }));
            if (user) { try { await funResult("price_guess", true); } catch { /* ignore */ } }
        }
    };

    const answerValue = async (pick) => {
        if (!round || phase !== "value") return;
        const ok = pick === round.has_value;
        setValueCorrect(ok);
        setPhase("done");
        setScore((s) => ({
            pts: s.pts + (ok ? 1 : 0),
            rounds: s.rounds + 1,
            streak: ok && priceCorrect ? s.streak + 1 : 0,
        }));
        if (ok && user) { try { await funResult("price_guess", true); } catch { /* ignore */ } }
    };

    const p = round?.player;
    const timePct = round ? Math.max(0, (timeLeft / round.time_limit) * 100) : 0;

    return (
        <AppShell>
            <div className="max-w-3xl mx-auto space-y-6" data-testid="price-guess-page">
                <header className="text-center">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow">Fun · Cenovka</div>
                    <h1 className="font-display font-black uppercase text-2xl md:text-3xl mt-1 flex items-center justify-center gap-2">
                        <Tag size={24} className="text-fa-yellow" /> Tipni aktuální cenu
                    </h1>
                    <div className="font-mono text-[10px] text-white/50 mt-2" data-testid="price-guess-score">
                        Body {score.pts} · kol {score.rounds} · streak {score.streak}
                        {user ? " · správně = +1 Knowledge XP" : " · přihlas se a sbírej Knowledge XP"}
                    </div>
                </header>

                {!difficulty && (
                    <div className="grid sm:grid-cols-3 gap-3" data-testid="price-guess-difficulty">
                        {DIFFS.map((d) => (
                            <button key={d.key} type="button"
                                    onClick={() => { setDifficulty(d.key); next(d.key); }}
                                    data-testid={`price-guess-diff-${d.key}`}
                                    className={`border bg-black/40 p-5 text-center space-y-1.5 hover:bg-white/[0.04] transition-colors ${d.color}`}>
                                <div className="font-display font-black uppercase text-lg">{d.label}</div>
                                <div className="font-mono text-[9px] text-white/55">{d.desc}</div>
                            </button>
                        ))}
                    </div>
                )}

                {difficulty && !round && <p className="font-mono text-[11px] text-white/40 text-center py-10">Načítám hráče…</p>}

                {round && p && (
                    <div className="border border-white/10 bg-[#0d0d0d] p-5 space-y-4" data-testid="price-guess-round">
                        {/* Hráč */}
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div>
                                <div className="font-display font-black text-2xl uppercase" data-testid="price-guess-player">{p.name}</div>
                                <div className="font-mono text-[10px] uppercase text-white/50 mt-0.5">
                                    {p.team} · {p.position} · {p.ownership}% own.
                                </div>
                            </div>
                            <button type="button" onClick={() => { setDifficulty(null); stopTimer(); setRound(null); }}
                                    data-testid="price-guess-change-diff"
                                    className="font-mono text-[9px] uppercase tracking-widest text-white/40 hover:text-white border border-white/15 px-2 py-1">
                                {DIFFS.find((d) => d.key === difficulty)?.label} · změnit
                            </button>
                        </div>
                        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                            {STAT_LABELS.map(([k, l]) => (
                                <div key={k} className="border border-white/10 bg-black/40 p-2 text-center">
                                    <div className="font-mono text-[7px] uppercase text-white/40">{l}</div>
                                    <div className="font-display font-bold text-base tabular-nums">{p.stats[k]}</div>
                                </div>
                            ))}
                        </div>

                        {/* Fáze 1 — tip ceny */}
                        {phase === "price" && (
                            <div className="space-y-3" data-testid="price-guess-input-phase">
                                <div className="h-1.5 bg-white/10">
                                    <div className={`h-full transition-[width] duration-100 ${timePct > 40 ? "bg-fa-mint" : timePct > 15 ? "bg-fa-yellow" : "bg-red-400"}`}
                                         style={{ width: `${timePct}%` }} />
                                </div>
                                <div className="flex items-center justify-center gap-2 font-mono text-[10px] text-white/50">
                                    <Timer size={12} /> {timeLeft.toFixed(1)} s · Jakou má aktuálně cenu v sezoně 26/27? (tolerance ±£{round.tolerance})
                                </div>
                                <div className="flex items-center justify-center gap-2">
                                    <button type="button" onClick={() => setGuess((g) => Math.max(3.5, Math.round((g - 0.5) * 10) / 10))}
                                            data-testid="price-guess-minus"
                                            className="font-display font-black text-lg border border-white/20 w-10 h-10 hover:border-fa-yellow">−</button>
                                    <div className="font-display font-black text-4xl tabular-nums text-fa-yellow w-32 text-center" data-testid="price-guess-value">
                                        £{guess.toFixed(1)}
                                    </div>
                                    <button type="button" onClick={() => setGuess((g) => Math.min(16, Math.round((g + 0.5) * 10) / 10))}
                                            data-testid="price-guess-plus"
                                            className="font-display font-black text-lg border border-white/20 w-10 h-10 hover:border-fa-yellow">+</button>
                                </div>
                                <input type="range" min={3.5} max={16} step={0.5} value={guess}
                                       onChange={(e) => setGuess(Number(e.target.value))}
                                       data-testid="price-guess-slider" className="w-full accent-[#FFF3C2]" />
                                <button type="button" onClick={submitPrice} data-testid="price-guess-submit"
                                        className="w-full font-mono text-[11px] uppercase tracking-widest px-4 py-2.5 bg-fa-yellow text-black font-bold">
                                    Tipnout
                                </button>
                            </div>
                        )}

                        {/* Fáze 2 — value otázka */}
                        {phase !== "price" && (
                            <div className={`border p-3 flex items-center gap-2 font-mono text-[11px] ${priceCorrect ? "border-fa-mint/50 bg-fa-mint/5 text-fa-mint" : "border-red-400/50 bg-red-500/5 text-red-300"}`}
                                 data-testid="price-guess-price-result">
                                {priceCorrect ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} weight="fill" />}
                                Skutečná cena: <strong className="font-display text-base">£{round.price.toFixed(1)}</strong>
                                {priceCorrect === false && timeLeft === 0 && <span className="text-white/45">· čas vypršel</span>}
                            </div>
                        )}
                        {phase === "value" && (
                            <div className="space-y-2" data-testid="price-guess-value-phase">
                                <div className="text-center font-display font-bold text-base">
                                    Má {p.name} za £{round.price.toFixed(1)} value?
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <button type="button" onClick={() => answerValue(true)} data-testid="price-guess-value-yes"
                                            className="font-mono text-[11px] uppercase tracking-widest px-4 py-3 border border-fa-mint/50 text-fa-mint hover:bg-fa-mint hover:text-black font-bold">
                                        Ano, má value
                                    </button>
                                    <button type="button" onClick={() => answerValue(false)} data-testid="price-guess-value-no"
                                            className="font-mono text-[11px] uppercase tracking-widest px-4 py-3 border border-red-400/50 text-red-300 hover:bg-red-400 hover:text-black font-bold">
                                        Ne, přeplacený
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Vyhodnocení */}
                        {phase === "done" && (
                            <div className="space-y-3" data-testid="price-guess-done">
                                <div className={`border p-3 flex items-center gap-2 font-mono text-[11px] ${valueCorrect ? "border-fa-mint/50 bg-fa-mint/5 text-fa-mint" : "border-red-400/50 bg-red-500/5 text-red-300"}`}
                                     data-testid="price-guess-value-result">
                                    {valueCorrect ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} weight="fill" />}
                                    <span>
                                        {round.has_value ? "MÁ value" : "NEMÁ value"} — {round.value_ratio} bodů/£m
                                        {" "}(hranice pro {p.position}: {round.value_threshold})
                                    </span>
                                </div>
                                <button type="button" onClick={() => next(difficulty)} data-testid="price-guess-next"
                                        className="w-full font-mono text-[11px] uppercase tracking-widest px-4 py-2.5 bg-fa-yellow text-black font-bold flex items-center justify-center gap-1.5">
                                    Další hráč <ArrowRight size={13} weight="bold" />
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </AppShell>
    );
}
